import type { SearchParams } from '@/hooks/useRouterQuery';

import { TeamUserAvatar } from '@poseidon/components/Avatar';
import type { TeamLogo } from '@poseidon/components/Avatar';
import type { CompanyOption, UserOption } from '@poseidon/components/Input2';
import { cn } from '@poseidon/components/cn';
import {
  Button,
  ButtonGroup,
  CampaignTemplateFilterSelect,
  CompanyFilterSelect,
  DateButton,
  FilterSelect,
  OrderFilterSelect,
  OwnerFilterSelect,
  ResponseFilterSelect,
  StageFilterSelect,
  StatesFilterSelect,
  TagFilterSelect,
  TEMPLATE_SORT_DEFAULT_VALUE,
  TemplateSortFilterSelect,
} from '@poseidon/components/Input2';
import { Communication, Files, General, Maps, Time, Users } from '@poseidon/components/StrokeIcons';

import { useEffect, useState } from 'react';
import type { FilterShow, OffsetPaginationType } from './types';
import { humanizeCandidateStatus } from '@/clientOnlyUtils/humanizeCandidateStatus';
import { useAuthContext } from '~/auth/auth.context';
import { useFilterDefaults } from '@/hooks/useFilterDefaults';
import { useRouterQuery } from '@/hooks/useRouterQuery';
import { getSettingsPermissions } from '~/hooks/settingsPermissions';
import { api } from '@/utils/api';
import { OffsetPaginator } from '~/components/OffsetPaginator';
import { ConnectedUserFilter, SignalMultiFilter, STAGE_ALL_OPTION_VALUE, StageFilter, StageMultiFilter } from './FilterControls';
import { PickFilterButtonPure } from './PickFilterButtonPure';
import StepsRunCountFilter from './StepsRunCountFilter';
import { DEFAULT_LIMIT, formatDate, resolveDateRange } from './utils';

export type CandidateStatusValue =
  | 'pending'
  | 'researching'
  | 'research_failed'
  | 'gathering_sources'
  | 'synthesizing'
  | 'synthesize_failed'
  | 'filter_failed'
  | 'personalize_failed'
  | 'rate_limited'
  | 'account_disconnected'
  | 'awaiting_capacity'
  | 'retrying'
  | 'pending_review'
  | 'rejected'
  | 'promoted'
  | 'removed';

// Filterable subset — we hide `personalizing` and `removed` because they
// don't make sense as standalone filter buckets from the staging UI. The
// blocked statuses ARE filterable — "show me everything that's rate-limited"
// is exactly the query this redesign enables. The three VERDICT buckets sit
// together at the end (Needs review / Rejected / Enrolled) — with the
// auto-loader they're the reviewer's main lenses. Labels come from the
// shared humanizer so a rename flows everywhere.
export const CANDIDATE_STATUS_OPTIONS: { value: CandidateStatusValue; label: string }[] = (
  ['pending', 'researching', 'research_failed', 'gathering_sources', 'synthesizing', 'synthesize_failed', 'filter_failed', 'personalize_failed', 'rate_limited', 'account_disconnected', 'awaiting_capacity', 'retrying', 'pending_review', 'rejected', 'promoted'] as const
).map((v) => ({ value: v, label: humanizeCandidateStatus(v) }));

export const CANDIDATE_STATUS_ALL_VALUE = '';

// Template-activity filter: client-side tier on /templates (Active / Idle /
// Stale / No activity). The empty string means "All".
type TemplateActivityValue = 'active' | 'idle' | 'stale' | 'no_activity';

const TEMPLATE_ACTIVITY_OPTIONS: { value: TemplateActivityValue; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'idle', label: 'Idle' },
  { value: 'stale', label: 'Stale' },
  { value: 'no_activity', label: 'No activity' },
];

const TEMPLATE_ACTIVITY_ALL_VALUE = '';

// Shared owner dropdown used by both the contact-owner (`userId`) and
// campaign-owner (`cadenceUserId`) filters. The value `'team'` renders the
// team name + logo (the "whole team / nobody specific" default).
function TeamOwnerSelect({
  userOptions,
  teamName,
  teamLogo,
  value,
  onChange,
  tooltip,
}: {
  userOptions: UserOption[];
  teamName: string;
  teamLogo?: TeamLogo | null;
  value: string;
  onChange: (value: string) => void;
  tooltip: string;
}) {
  return (
    <OwnerFilterSelect
      size="sm"
      userOptions={userOptions}
      teamOption={{
        label: teamName,
        icon: (
          <TeamUserAvatar
            userTeam={{ profilePicture: teamLogo?.url, userTeamName: teamName }}
            className="size-full"
            shape="circle"
          />
        ),
      }}
      value={value}
      defaultValue="team"
      onChange={onChange}
      tooltip={tooltip}
    />
  );
}

interface Filters2Props {
  teamId: string;
  teamName: string;
  userId: string;
  teamLogo?: TeamLogo | null;
  pagination?: OffsetPaginationType;
  show: FilterShow[];
  className?: string;
  end?: React.ReactNode;
  orgOptions?: CompanyOption[];
  allowOwnerFilterForAll?: boolean;
  /**
   * When set, the contact-owner (`userId`) filter renders with this label in a
   * ButtonGroup, parallel to the `cadenceUserId` "Campaign owner" group. Used on
   * Connection Results (where it's "Connected" — whose LinkedIn connections you're
   * viewing) to disambiguate it from the campaign-owner filter. Left undefined on
   * other pages, where the bare owner dropdown is shown.
   */
  userIdLabel?: string;
  /**
   * Counts to display next to each tab when `'candidateStatus'` is in `show`.
   * Keyed by candidate status; missing keys render as `(0)`.
   */
  candidateStatusCounts?: Partial<Record<CandidateStatusValue, number>>;
  /**
   * Counts for the Active/All enrollment toggle when `'enrollment'` is in `show`.
   * `active` = cadences with is_active = true; `all` = every non-deleted cadence.
   */
  enrollmentCounts?: { active: number; all: number };
}

export default function Filters2({
  teamId,
  teamName,
  userId,
  teamLogo,
  pagination,
  show,
  className,
  end,
  orgOptions,
  allowOwnerFilterForAll = false,
  userIdLabel,
  candidateStatusCounts,
  enrollmentCounts,
}: Filters2Props) {
  const [query, , updateQuery] = useRouterQuery();
  const { fromDate, toDate } = resolveDateRange(query);

  // Permission gate: only admins/superadmins (incl. impersonating superadmins) can
  // change the owner filter. Regular users always see only their own data — the
  // dropdown is hidden and the URL is normalized to their own userId.
  const [auth] = useAuthContext();
  const userTeam = 'userTeam' in auth ? auth.userTeam : null;
  const { isAdmin } = getSettingsPermissions(
    {
      userId,
      isAdmin: userTeam?.isAdmin ?? false,
      isSuperAdmin: userTeam?.isSuperAdmin ?? false,
    },
    userId,
    auth.impersonation != null,
  );
  const canSeeOwnerFilter = isAdmin || allowOwnerFilterForAll;
  const effectiveShow: FilterShow[] = canSeeOwnerFilter
    ? show
    : show.filter((f) => f !== 'userId' && f !== 'cadenceUserId');
  const { saveDefaults, clearDefaults } = useFilterDefaults(teamId, effectiveShow);

  // For non-admins, force `userId` query param to their own id so all downstream
  // consumers (cadences, contacts, etc.) only fetch their own data. Opt-out via
  // `allowOwnerFilterForAll` for shared-resource pages (e.g. Content Library).
  useEffect(() => {
    if (!canSeeOwnerFilter && show.includes('userId') && query.userId !== userId) {
      updateQuery({ userId });
    }
  }, [canSeeOwnerFilter, show, query.userId, userId, updateQuery]);

  const showsUserId = effectiveShow.includes('userId');
  const showsCadenceUserId = effectiveShow.includes('cadenceUserId');
  const showsConnectedUserId = show.includes('connectedUserId');
  const showsTag = show.includes('tag') || show.includes('contactTag');
  const showsTemplateId = show.includes('templateId');
  const showsStage = show.includes('stageId') || show.includes('stageId2') || show.includes('stageIds');

  const filtersQuery = api.filters.data.useQuery({
    teamId,
    includeUserOptions: showsUserId || showsCadenceUserId,
    includeTeamUsers: showsConnectedUserId,
    includeTags: showsTag,
    includeTemplates: showsTemplateId,
    includeStages: showsStage,
  });

  const userOptions = filtersQuery.data?.userOptions ?? [];
  const tagOptions = filtersQuery.data?.tags?.map((tag) => ({ value: tag.id, label: tag.name })) ?? [];
  const contactTagOptions =
    filtersQuery.data?.tags
      ?.filter((tag) => tag.contactOnly)
      .map((tag) => ({ value: tag.id, label: tag.name })) ?? [];
  const templates =
    filtersQuery.data?.templates?.map((d) => ({
      id: d.id,
      name: d.name,
      userId: d.userId,
    })) ?? [];
  const stages = filtersQuery.data?.stages ?? [];

  const [selectedFilter, setSelectedFilter] = useState<FilterShow | null>(null);

  const updateFilters = (changes: Partial<SearchParams>) => {
    const newQuery = { ...query, ...changes, offset: undefined };
    updateQuery({ ...changes, offset: undefined });
    saveDefaults(newQuery);
  };

  const handleDateRangeChange = ({ from, to }: { from?: string; to?: string }) => {
    const nextFrom = from ?? formatDate(fromDate);
    const nextTo = to ?? formatDate(toDate);
    const nextRange = `${nextFrom}.${nextTo}`;
    updateFilters({ dateRange: nextRange, from: undefined, to: undefined });
  };

  const isFilterActive = (filter: FilterShow) => {
    switch (filter) {
      case 'userId':
      case 'dateRange':
        return true;
      case 'connectedUserId':
        return query.connectedUserId != null;
      case 'connectionAccepted':
        return query.connectionAccepted != null;
      case 'connectionType':
        return query.connectionType != null && query.connectionType !== 'all';
      case 'stepsRunCount':
        return query.stepsRunCount != null;
      case 'tag':
      case 'contactTag':
        return query.tag != null;
      case 'templateId':
        return query.templateId != null;
      case 'stageId':
        return query.stageId != null;
      case 'stageId2':
        return query.stageId != null;
      case 'stageIds':
        return query.stageIds != null && query.stageIds.length > 0;
      case 'signalBuckets':
        return query.signalBuckets != null && query.signalBuckets.length > 0;
      case 'order':
        return query.order != null;
      case 'states':
        return query.states != null;
      case 'response':
        return query.response != null;
      case 'companyId':
        return query.companyId != null;
      case 'templateSort':
        return true;
      case 'recentlyConnected':
        return query.recentlyConnected === true;
      case 'messagesSentMax':
        return query.messagesSentMax != null;
      case 'candidateStatus':
        // Always show the candidate-status tabs in the toolbar; the "All" tab
        // represents the no-filter state.
        return true;
      case 'templateActivity':
        // Always show — the "All" option represents the no-filter state.
        return true;
      default:
        return false;
    }
  };

  const renderFilter = (filter: FilterShow) => {
    switch (filter) {
      case 'userId':
        if (userIdLabel == null) {
          return (
            <TeamOwnerSelect
              key="userId"
              userOptions={userOptions}
              teamName={teamName}
              teamLogo={teamLogo}
              value={query.userId || 'team'}
              onChange={(value) => updateFilters({ userId: value === 'team' ? undefined : value })}
              tooltip="Change owner"
            />
          );
        }
        return (
          <ButtonGroup key="userId" size="sm">
            <Button readOnly icon={<Users.User01 />}>
              {userIdLabel}
            </Button>
            <TeamOwnerSelect
              userOptions={userOptions}
              teamName={teamName}
              teamLogo={teamLogo}
              value={query.userId || 'team'}
              onChange={(value) => updateFilters({ userId: value === 'team' ? undefined : value })}
              tooltip="Change owner"
            />
          </ButtonGroup>
        );
      case 'cadenceUserId':
        return (
          <ButtonGroup key="cadenceUserId" size="sm">
            <Button readOnly icon={<Maps.Rocket />}>
              Campaign owner
            </Button>
            <TeamOwnerSelect
              userOptions={userOptions}
              teamName={teamName}
              teamLogo={teamLogo}
              value={query.cadenceUserId || 'team'}
              onChange={(value) => updateFilters({ cadenceUserId: value === 'team' ? undefined : value })}
              tooltip="Filter by campaign owner"
            />
          </ButtonGroup>
        );
      case 'connectedUserId': {
        const value = query.connectedUserId;
        if (value == null) return null;
        if (value === '__any__') {
          return (
            <ButtonGroup key="connectedUserId" size="sm">
              <Button icon={<Users.Users01 />}>Connected to Anyone</Button>
              <Button icon={<General.XMark />} onClick={() => updateFilters({ connectedUserId: undefined })} />
            </ButtonGroup>
          );
        }
        return (
          <ButtonGroup key="connectedUserId" size="sm">
            <Button icon={<Users.UserPlus01 />}>Connected to</Button>
            <ConnectedUserFilter
              size="sm"
              userId={userId}
              value={value}
              teamUsers={filtersQuery.data?.teamUsers ?? null}
              isPending={filtersQuery.isLoading}
              onChange={({ value }) => updateFilters({ connectedUserId: value })}
            />
            <Button icon={<General.XMark />} onClick={() => updateFilters({ connectedUserId: undefined })} />
          </ButtonGroup>
        );
      }
      case 'connectionAccepted': {
        const value = query.connectionAccepted;
        if (value == null) return null;
        return (
          <ButtonGroup key="connectionAccepted" size="sm">
            <Button icon={<Users.UserPlus01 />}>{value === 'accepted' ? 'Connected' : 'Connection Sent'}</Button>
            <Button icon={<General.XMark />} onClick={() => updateFilters({ connectionAccepted: undefined })} />
          </ButtonGroup>
        );
      }
      case 'connectionType': {
        const value = query.connectionType;
        if (value == null || value === 'all') return null;
        return (
          <ButtonGroup key="connectionType" size="sm">
            <Button icon={<Communication.UserCheckSquare />}>
              {value === 'external' ? 'External Connection' : 'Connected via Poseidon'}
            </Button>
            <Button icon={<General.XMark />} onClick={() => updateFilters({ connectionType: undefined })} />
          </ButtonGroup>
        );
      }
      case 'stepsRunCount': {
        const count = query.stepsRunCount;
        if (count == null) return null;
        return (
          <ButtonGroup key="stepsRunCount" size="sm">
            <Button icon={<General.Hash01 />}>Steps Run</Button>
            <StepsRunCountFilter
              size="sm"
              count={count}
              operation={query.stepsRunCountOperation ?? '=='}
              onChange={(count: number, operation: '==' | '<=' | '>=') =>
                updateFilters({ stepsRunCount: count, stepsRunCountOperation: operation })
              }
            />
            <Button
              icon={<General.XMark />}
              onClick={() => updateFilters({ stepsRunCount: undefined, stepsRunCountOperation: undefined })}
            />
          </ButtonGroup>
        );
      }
      case 'tag': {
        const value = query.tag ?? '';
        if (!value) return null;
        return (
          <TagFilterSelect
            key="tag"
            size="sm"
            tagOptions={tagOptions}
            value={value}
            defaultValue=""
            onChange={(value) => updateFilters({ tag: value || undefined })}
          />
        );
      }
      case 'contactTag': {
        const value = query.tag ?? '';
        if (!value) return null;
        return (
          <TagFilterSelect
            key="contactTag"
            size="sm"
            tagOptions={contactTagOptions}
            value={value}
            defaultValue=""
            onChange={(value) => updateFilters({ tag: value || undefined })}
          />
        );
      }
      case 'templateId': {
        const value = query.templateId ?? '';
        return (
          <CampaignTemplateFilterSelect
            key="templateId"
            size="sm"
            templates={templates}
            includeFilterOptions
            renderOwnerIcon={(template) => (
              <TeamUserAvatar
                userTeam={{ profilePicture: template.ownerProfilePicture, userTeamName: template.ownerName }}
                className="size-full"
                shape="circle"
              />
            )}
            value={value}
            defaultValue=""
            onChange={(value) => updateFilters({ templateId: value || undefined })}
          />
        );
      }
      case 'stageId': {
        const value = query.stageId;
        return (
          <ButtonGroup key="stageId" size="sm">
            <Button tooltip="Stage" icon={<Files.StickerCircle />} />
            <StageFilter
              size="sm"
              value={value}
              stages={stages}
              isPending={filtersQuery.isLoading}
              onChange={({ value }) => {
                updateFilters({ stageId: value === STAGE_ALL_OPTION_VALUE ? undefined : value });
              }}
            />
          </ButtonGroup>
        );
      }
      case 'stageId2': {
        const value = query.stageId ?? '';
        return (
          <StageFilterSelect
            key="stageId2"
            size="sm"
            stages={stages}
            value={value}
            defaultValue=""
            onChange={(value) => {
              updateFilters({ stageId: value || undefined });
            }}
          />
        );
      }
      case 'stageIds': {
        const value = query.stageIds ?? [];
        if (value.length === 0) return null;
        return (
          <ButtonGroup key="stageIds" size="sm">
            <Button tooltip="Stage" icon={<Files.StickerCircle />} />
            <StageMultiFilter
              size="sm"
              value={value}
              stages={stages}
              isPending={filtersQuery.isLoading}
              onChange={(next) => updateFilters({ stageIds: next.length > 0 ? next : undefined })}
            />
            <Button icon={<General.XMark />} onClick={() => updateFilters({ stageIds: undefined })} />
          </ButtonGroup>
        );
      }
      case 'signalBuckets': {
        const value = query.signalBuckets ?? [];
        if (value.length === 0) return null;
        return (
          <ButtonGroup key="signalBuckets" size="sm">
            <Button tooltip="Signal" icon={<Communication.Rss01 />} />
            <SignalMultiFilter
              size="sm"
              value={value}
              onChange={(next) => updateFilters({ signalBuckets: next.length > 0 ? next : undefined })}
            />
            <Button icon={<General.XMark />} onClick={() => updateFilters({ signalBuckets: undefined })} />
          </ButtonGroup>
        );
      }
      case 'companyId': {
        const value = query.companyId ?? '';
        if (!value) return null;
        return (
          <CompanyFilterSelect
            key="companyId"
            size="sm"
            companyOptions={orgOptions ?? []}
            value={value}
            defaultValue=""
            onChange={(value) => updateFilters({ companyId: value || undefined })}
          />
        );
      }
      case 'dateRange':
        return (
          <ButtonGroup key="dateRange" size="sm">
            <Button icon={<Time.CalendarDate />} />
            <DateButton
              size="sm"
              value={fromDate.valueOf()}
              onDateChange={(from: string) => handleDateRangeChange({ from })}
              hideCurrentYear
              icon={null}
            />
            <DateButton
              size="sm"
              value={toDate.valueOf()}
              onDateChange={(to: string) => handleDateRangeChange({ to })}
              hideCurrentYear
              icon={null}
            />
          </ButtonGroup>
        );
      case 'order': {
        const value = query.order ?? '';
        if (!value) return null;
        return (
          <OrderFilterSelect
            key="order"
            size="sm"
            value={value}
            defaultValue=""
            onChange={(value) => updateFilters({ order: value ? (value as SearchParams['order']) : undefined })}
          />
        );
      }
      case 'states': {
        const value = query.states ?? '';
        if (!value) return null;
        return (
          <StatesFilterSelect
            key="states"
            size="sm"
            value={value}
            defaultValue=""
            onChange={(value) => updateFilters({ states: value ? (value as SearchParams['states']) : undefined })}
          />
        );
      }
      case 'response': {
        const value = query.response ?? '';
        if (!value) return null;
        return (
          <ResponseFilterSelect
            key="response"
            size="sm"
            value={value}
            defaultValue=""
            onChange={(value) => updateFilters({ response: value ? (value as SearchParams['response']) : undefined })}
          />
        );
      }
      case 'candidateStatus': {
        const value = query.candidateStatus ?? CANDIDATE_STATUS_ALL_VALUE;
        const options = [
          { value: CANDIDATE_STATUS_ALL_VALUE, label: 'All statuses' },
          ...CANDIDATE_STATUS_OPTIONS.map((o) => ({
            value: o.value,
            label: `${o.label} (${candidateStatusCounts?.[o.value] ?? 0})`,
          })),
        ];
        return (
          <FilterSelect
            key="candidateStatus"
            size="sm"
            options={options}
            value={value}
            placeholder="Staging status"
            onChange={(v) =>
              updateFilters({
                candidateStatus: v === CANDIDATE_STATUS_ALL_VALUE ? undefined : (v as CandidateStatusValue),
              })
            }
            filterable={false}
            autoWidth
          />
        );
      }
      case 'templateActivity': {
        const value = query.templateActivity ?? TEMPLATE_ACTIVITY_ALL_VALUE;
        const options = [
          { value: TEMPLATE_ACTIVITY_ALL_VALUE, label: 'All campaigns' },
          ...TEMPLATE_ACTIVITY_OPTIONS,
        ];
        return (
          <FilterSelect
            key="templateActivity"
            size="sm"
            options={options}
            value={value}
            placeholder="Activity"
            onChange={(v) =>
              updateFilters({
                templateActivity:
                  v === TEMPLATE_ACTIVITY_ALL_VALUE ? undefined : (v as TemplateActivityValue),
                offset: undefined,
              })
            }
            filterable={false}
            autoWidth
          />
        );
      }
      case 'enrollment': {
        // Coarse scope toggle for the enrolled list. 'active' (default) shows each
        // contact's one live cadence; 'all' reveals every non-deleted enrollment.
        // Always visible so the user can always see — and switch — which set they're
        // viewing. The 'All' segment is the way back to the full list.
        const value = query.enrollment ?? 'active';
        const activeLabel = enrollmentCounts ? `Active · ${enrollmentCounts.active}` : 'Active';
        const allLabel = enrollmentCounts ? `All · ${enrollmentCounts.all}` : 'All';
        return (
          <ButtonGroup key="enrollment" size="sm">
            <Button
              icon={<Users.Users01 />}
              color={value === 'active' ? 'blue' : 'gray'}
              tone={value === 'active' ? 'filled' : 'soft'}
              onClick={() => updateFilters({ enrollment: undefined })}
              tooltip="People currently in this campaign — each contact's one active cadence">
              {activeLabel}
            </Button>
            <Button
              color={value === 'all' ? 'blue' : 'gray'}
              tone={value === 'all' ? 'filled' : 'soft'}
              onClick={() => updateFilters({ enrollment: 'all' })}
              tooltip="Everyone ever enrolled, including finished, paused, and past re-enrollments">
              {allLabel}
            </Button>
          </ButtonGroup>
        );
      }
      case 'templateSort':
        return (
          <TemplateSortFilterSelect
            key="templateSort"
            size="sm"
            value={query.templateSort ?? TEMPLATE_SORT_DEFAULT_VALUE}
            defaultValue={TEMPLATE_SORT_DEFAULT_VALUE}
            onChange={(value) =>
              updateFilters({
                templateSort:
                  value === TEMPLATE_SORT_DEFAULT_VALUE ? undefined : (value as SearchParams['templateSort']),
              })
            }
          />
        );
      case 'recentlyConnected': {
        const isActive = query.recentlyConnected === true;
        return (
          <ButtonGroup key="recentlyConnected" size="sm">
            <Button
              onClick={() => updateFilters({ recentlyConnected: isActive ? undefined : true })}
              icon={<Time.Clock />}
              tooltip="Contacts connected in the last 7 days">
              Recent
            </Button>
            {isActive && (
              <Button icon={<General.XMark />} onClick={() => updateFilters({ recentlyConnected: undefined })} />
            )}
          </ButtonGroup>
        );
      }
      case 'messagesSentMax': {
        const value = query.messagesSentMax;
        if (!value) return null;
        return (
          <ButtonGroup key="messagesSentMax" size="sm">
            <FilterSelect
              size="sm"
              icon={<Communication.MessageSquare01 />}
              options={[
                { value: '1', label: '≤ 1 message' },
                { value: '2', label: '≤ 2 messages' },
                { value: '3', label: '≤ 3 messages' },
                { value: '4+', label: '4+ messages' },
              ]}
              value={value}
              onChange={(newValue) => {
                if (newValue === '1' || newValue === '2' || newValue === '3' || newValue === '4+') {
                  updateFilters({ messagesSentMax: newValue });
                }
              }}
              tooltip="Messages Sent"
            />
            <Button icon={<General.XMark />} onClick={() => updateFilters({ messagesSentMax: undefined })} />
          </ButtonGroup>
        );
      }
      default:
        return null;
    }
  };

  const ALWAYS_VISIBLE_FILTERS: ReadonlySet<FilterShow> = new Set([
    'userId',
    'cadenceUserId',
    'dateRange',
    'stageId',
    'stageId2',
    'templateId',
    'templateSort',
    'candidateStatus',
    'templateActivity',
    'enrollment',
  ]);

  const activeFilters = effectiveShow.filter(
    (filter) => ALWAYS_VISIBLE_FILTERS.has(filter) || isFilterActive(filter),
  );
  const availableFilters = effectiveShow.filter(
    (filter) => !ALWAYS_VISIBLE_FILTERS.has(filter) && !isFilterActive(filter),
  );

  const hasFilters = effectiveShow.some((key) => {
    if (key === 'userId' || key === 'cadenceUserId' || key === 'dateRange' || key === 'templateId') {
      return false;
    }
    if (key === 'stageId' || key === 'stageId2') {
      return query.stageId != null;
    }
    if (key === 'stageIds') {
      return query.stageIds != null && query.stageIds.length > 0;
    }
    if (key === 'signalBuckets') {
      return query.signalBuckets != null && query.signalBuckets.length > 0;
    }
    return isFilterActive(key);
  });
  const hasUnselectedFilters = availableFilters.length > 0;

  useEffect(() => {
    if (selectedFilter && !availableFilters.includes(selectedFilter)) {
      setSelectedFilter(null);
    }
  }, [availableFilters, selectedFilter]);

  const clearFilters = () => {
    const clearObj = Object.fromEntries(effectiveShow.map((k) => [k, undefined])) as Partial<SearchParams>;
    // Also clear stepsRunCountOperation when clearing stepsRunCount
    if (effectiveShow.includes('stepsRunCount')) {
      clearObj.stepsRunCountOperation = undefined;
    }
    if (effectiveShow.includes('stageId2')) {
      clearObj.stageId = undefined;
    }
    if (effectiveShow.includes('stageIds')) {
      clearObj.stageIds = undefined;
    }
    if (effectiveShow.includes('signalBuckets')) {
      clearObj.signalBuckets = undefined;
    }
    updateQuery({ ...clearObj, offset: undefined });
    clearDefaults();
  };

  return (
    <nav
      className={cn(
        'flex flex-row border-t border-b p-2 px-8 gap-1 bg-slate-100  border-slate-200 dark:bg-charade-950 dark:border-charade-900',
        className,
      )}>
      {activeFilters.map((filter) => renderFilter(filter))}

      {hasUnselectedFilters && (
        <PickFilterButtonPure
          teamId={teamId}
          teamName={teamName}
          userId={userId}
          teamLogo={teamLogo}
          availableFilters={availableFilters}
          orgOptions={orgOptions}
          selectedFilter={selectedFilter}
          onSelectFilter={setSelectedFilter}
          onFilterChange={(changes) => {
            updateFilters(changes);
            setSelectedFilter(null);
          }}
          tags={filtersQuery.data?.tags ?? []}
          teamUsers={filtersQuery.data?.teamUsers ?? []}
          templates={templates}
          stages={stages}
        />
      )}

      {hasFilters && (
        <Button size="sm" icon={<General.XMark />} text onClick={clearFilters}>
          Clear
        </Button>
      )}
      <div className="grow" />
      {end}
      {pagination && (
        <OffsetPaginator
          limit={query.limit}
          defaultLimit={DEFAULT_LIMIT}
          offset={pagination.offset}
          len={pagination.len}
          onChange={(next) => updateQuery(next)}
        />
      )}
    </nav>
  );
}
